import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Pagination from "@mui/material/Pagination";
import DialogBox from "components/lib-ui/DialogBox";
import BlankState from "../BlankState";
import AddIcon from "@mui/icons-material/Add";
import AddNewRecuitmentProcess from "../../candidate-form/recruitment-processes/AddNewRecuitmentProcess";
import RecruitmentProcessCard from "../RecruitmentProcessCard";
import { useRecruitersAccounts } from "hooks/useRecruitersAccounts";
import { useSelector } from "react-redux";
import { useRecruitmentProcess } from "hooks/useRecruitmentProcess";
import ExpandableButton from "components/lib-ui/ExpandableButton";

export const RecruitmentProcesses = ({
    customButtonStyling = {},
}) => {
    useRecruitersAccounts();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { changePage } = useRecruitmentProcess();

    const {
        items: recruitmentProcesses,
        isInitialLoading,
        isFetchingPage,
        isLoadingProcesses,
        meta,
        currentPage,
    } = useSelector((state) => state.recruitmentProcesses);

    const { id: candidateId } = useSelector((state) => state.currentCandidate);

    const handlePageChange = (_event, newPage) => {
        changePage(candidateId, newPage);
    };

    const totalPages = meta?.last_page || 1;
    const totalItems = meta?.total || 0;
    const currentPageStart = meta?.from || 0;
    const currentPageEnd = meta?.to || 0;

    return (
        <Box
            sx={{
                maxWidth: "1200px",
            }}
        >
            {isInitialLoading && (
                <div data-testid="recruitmentProcess/loading">
                    <CircularProgress />
                </div>
            )}

            {!isInitialLoading && (
                <>
                    {/* If there are no recruitmentProcesses, return the BlankState */}
                    {recruitmentProcesses?.length === 0 && (
                        <Box>
                            <BlankState
                                text="No active recruitment processes"
                                showButton={true}
                                dataTestId="recruitmentProcess/create"
                                buttonText="Create recruitment process"
                                onClick={() => {
                                    setIsDialogOpen(true);
                                }}
                            />
                        </Box>
                    )}

                    <DialogBox
                        isDialogOpen={isDialogOpen}
                        setIsDialogOpen={setIsDialogOpen}
                        title="Create recruitment process"
                        floatCancel={true}
                        showActionButtons={false}
                    >
                        <AddNewRecuitmentProcess
                            setIsDialogOpen={setIsDialogOpen}
                        />
                    </DialogBox>

                    {recruitmentProcesses?.length > 0 && (
                        <Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 3,
                                    flexDirection: "column",
                                }}
                            >
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Typography
                                        variant="header2"
                                        component="h3"
                                    >
                                        Active
                                    </Typography>

                                    <Typography variant="subtitle2">
                                        Showing {currentPageStart}-
                                        {currentPageEnd} of {totalItems}
                                    </Typography>
                                </Stack>

                                {recruitmentProcesses.map(
                                    (recruitmentProcessData, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                opacity:
                                                    isLoadingProcesses ||
                                                    isFetchingPage
                                                        ? 0.2
                                                        : 1,
                                                transition:
                                                    "opacity 0.2s ease-in-out",
                                                pointerEvents:
                                                    isLoadingProcesses
                                                        ? "none"
                                                        : "auto",
                                            }}
                                        >
                                            <RecruitmentProcessCard
                                                recruitmentProcessData={
                                                    recruitmentProcessData
                                                }
                                            />
                                        </Box>
                                    ),
                                )}

                                {totalPages > 1 && (
                                    <Stack
                                        direction="row"
                                        justifyContent="center"
                                        alignItems="center"
                                        mt={3}
                                    >
                                        <Pagination
                                            page={currentPage}
                                            count={totalPages}
                                            onChange={handlePageChange}
                                            disabled={isFetchingPage}
                                            boundaryCount={2}
                                            siblingCount={1}
                                            showFirstButton
                                            showLastButton
                                        />
                                    </Stack>
                                )}
                            </Box>
                        </Box>
                    )}

                    <Box
                        sx={{
                            position: "fixed",
                            right: "30px",
                            bottom: "30px",
                            ...customButtonStyling,
                        }}
                    >
                        {!isDialogOpen && (
                            <ExpandableButton
                                dataTestId="recruitmentProcess/create"
                                text="Create recruitment process"
                                icon={AddIcon}
                                onClick={() => {
                                    setIsDialogOpen(true);
                                }}
                            />
                        )}
                    </Box>
                </>
            )}
        </Box>
    );
};

export default RecruitmentProcesses;
